import styled from '@xstyled/styled-components';
import React from 'react';
import { t } from 'ttag';
import { VisuallyHiddenLegend } from '~/lib/a11y/VisuallyHidden';
import { sanitizeHtmlForDisplay, stripHtml } from '~/lib/sanitization';
import { usePretestAnswers } from '~/training/sequence/PretestAnswers';
import { QuizButton } from '~/ui/buttons/QuizButton';
import {
  drawnQuestion,
  PretestQuestion,
  questionOptions
} from './pretestSlide';

// Sized and shaped like the quiz card the templates draw.
const Card = styled.div`
  box-sizing: border-box;
  width: calc(100% - 32px);
  max-width: 420px;
  margin: auto;
  padding: lg;
  border-radius: 20px;
  background: ${p => p.theme.colors.white};
  border: 1px solid var(--color-modal-edge, transparent);
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.05),
    0 30px 30px rgba(0, 0, 0, 0.03),
    0 15px 20px rgba(0, 0, 0, 0.05);
  color: ${p => p.theme.colors.black};
`;

const Title = styled.h3`
  margin: 0 0 lg;
  font-family: recoleta;
  font-size: 24px;
  font-weight: 400;
  line-height: 32px;
`;

const Body = styled.p`
  margin: 0 0 md;
`;

const Fieldset = styled.fieldset`
  border: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: md;
`;

const Picture = styled.img`
  display: block;
  max-width: 100%;
  max-height: 240px;
  margin: 0 auto md;
  border-radius: md;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 14px;
`;

/**
 * Asks the drawn question on its own, for slides whose template cannot ask it
 * through answer buttons (see canAskWithTemplate). Choices go to the pretest
 * answers like the template's buttons would.
 */
export const PretestQuestionView = ({
  question
}: {
  question: PretestQuestion;
}) => {
  const answers = usePretestAnswers();
  const item = drawnQuestion(question);
  const { multiple } = questionOptions(question);
  const picture = item?.pictures?.find(p => p.mediaUrl);
  const title = item?.title || '';

  return (
    <Card data-test-id="pretest-question">
      {picture && <Picture src={picture.mediaUrl!} alt="" />}
      {title && (
        <Title
          dangerouslySetInnerHTML={{ __html: sanitizeHtmlForDisplay(title) }}
        />
      )}
      {item?.body && (
        <Body
          dangerouslySetInnerHTML={{
            __html: sanitizeHtmlForDisplay(item.body)
          }}
        />
      )}
      <Fieldset>
        <VisuallyHiddenLegend>{stripHtml(title)}</VisuallyHiddenLegend>
        {multiple && <Hint>{t`Select all the correct answers`}</Hint>}
        {(item?.items ?? []).map(option => {
          const chosen = !!answers?.isChosen(option.id);
          return (
            <QuizButton
              key={option.id}
              type="button"
              variant={chosen ? 'chosen' : 'default'}
              aria-pressed={chosen}
              disabled={answers?.disabled}
              onClick={() => answers?.choose(option.id)}
              data-test-id="pretest-option"
            >
              <span>{option.value}</span>
            </QuizButton>
          );
        })}
      </Fieldset>
    </Card>
  );
};
